import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { mxn } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Sparkles, Award, FolderKanban, Coins } from "lucide-react";

export default function MyRecognition() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/users/me/recognition").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="text-sm text-[#5C6A7D]">Loading…</div>;

  const roleLabel = { admin: "Administrator", project_manager: "Gerente de Proyecto", cost_dept: "Cost Department" }[user?.role] || user?.role;

  return (
    <div className="space-y-8" data-testid="recognition-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Recognition</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[#0A2540] tracking-tighter mt-1">{data.user.name}</h1>
          <div className="text-sm text-[#5C6A7D] mt-1">{roleLabel} · {data.user.email}</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E2E8F0] p-6 rounded-sm">
          <div className="flex items-center justify-between text-[#5C6A7D]">
            <div className="text-[10px] uppercase tracking-[0.2em]">Proyectos participados</div>
            <FolderKanban size={16} strokeWidth={1.5} />
          </div>
          <div className="kpi-value text-4xl text-[#0A2540] mt-3">{data.projects_count}</div>
        </div>
        <div className="bg-white border border-[#E2E8F0] p-6 rounded-sm">
          <div className="flex items-center justify-between text-[#5C6A7D]">
            <div className="text-[10px] uppercase tracking-[0.2em]">Puntos totales de proyectos</div>
            <Trophy size={16} strokeWidth={1.5} />
          </div>
          <div className="kpi-value text-4xl text-[#0A2540] mt-3">{data.total_points.toLocaleString()}</div>
        </div>
        <div className="bg-[#0A2540] text-white p-6 rounded-sm">
          <div className="flex items-center justify-between text-white/70">
            <div className="text-[10px] uppercase tracking-[0.2em]">Ahorro generado</div>
            <Coins size={16} strokeWidth={1.5} />
          </div>
          <div className="kpi-value text-4xl mt-3">{mxn(data.total_savings)}</div>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Award size={16} className="text-[#0052CC]" strokeWidth={1.5} />
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]">Insignias de equipo por participación</div>
        </div>
        {data.badge_recognitions.length === 0 ? (
          <div className="text-sm text-[#5C6A7D] py-6 text-center">
            <Sparkles className="mx-auto mb-2 text-[#5C6A7D]" size={20} strokeWidth={1.5} />
            Sin insignias todavía. Earn recognition by contributing to projects that reuse, share, and recover surplus.
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="badge-recognitions">
            {data.badge_recognitions.map((r, i) => (
              <li key={i} className="flex items-center gap-3 p-3 bg-[#FAFAFA] border border-[#E2E8F0] rounded-sm">
                <div className="w-9 h-9 rounded-sm bg-[#0A2540]/8 text-[#0A2540] flex items-center justify-center"><Award size={16} strokeWidth={1.5} /></div>
                <div className="text-sm font-semibold text-[#0A2540]">{r}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D] mb-3">Mis proyectos</div>
        {data.projects.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-10 text-center text-sm text-[#5C6A7D]">Todavía no has subido activos a ningún proyecto.</div>
        ) : (
          <div className="bg-white border border-[#E2E8F0] rounded-sm divide-y divide-[#E2E8F0]" data-testid="my-projects">
            {data.projects.map((p) => (
              <Link key={p.project_id} to={`/projects/${p.project_id}/impact`} className="flex items-center justify-between px-6 py-4 hover:bg-[#FAFAFA]/60">
                <div>
                  <div className="font-display text-lg font-bold text-[#0A2540]">{p.name}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {p.earned_badges.length === 0 && <span className="text-xs text-[#5C6A7D]">Sin insignias todavía</span>}
                    {p.earned_badges.map((b) => <span key={b} className="px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm bg-[#0A2540]/8 text-[#0A2540]">{b}</span>)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-extrabold text-2xl text-[#0A2540]">{p.points.toLocaleString()}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D]">{mxn(p.money_saved)} ahorrados</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
